import { useMemo, useState } from 'react';
import type { ApiEndpoints, NisStatementEventWithDocument } from '@avisoma/shared';
import { Card } from '../../components/Card';
import { DescriptionList, DescriptionTerm, DescriptionDetails } from '../../catalyst/description-list';
import { NisTable } from '../../components/reconstructedDocument/extractionTables/NisTable';
import { ExtractionTableCard } from '../../components/reconstructedDocument/extractionTables/ExtractionTableCard';
import { RowInspectorDialog } from '../../components/reconstructedDocument/extractionTables/RowInspectorDialog';
import { SourceCard, type SourceCardSource } from './SourceCard';

interface NisEventsProps {
  response: ApiEndpoints<string>['/api/projects/:id']['GET']['response'];
}

function sourcesFromNisEvents(events: NisStatementEventWithDocument[]): SourceCardSource[] {
  const byDocument = new Map<string, SourceCardSource>();
  for (const event of events) {
    const key = event.reconstructedDocumentId || event.documentId;
    const existing = byDocument.get(key);
    const pageIds = event.pageIds ?? [];
    if (existing) {
      byDocument.set(key, {
        ...existing,
        pageIds: [...existing.pageIds, ...pageIds.filter((id) => !existing.pageIds.includes(id))],
      });
      continue;
    }
    byDocument.set(key, {
      esn: event.esn,
      documentId: event.documentId,
      pageIds: [...pageIds],
    });
  }
  return [...byDocument.values()];
}

export function NisEvents({ response }: NisEventsProps) {
  const rows = response.item.data?.nisEvents?.nisEvents;
  const [inspected, setInspected] = useState<unknown>(null);

  const pageIdToPosition = useMemo(() => {
    const map = new Map<string, number>();
    rows?.forEach((row) => {
      (row.pageIds ?? []).forEach((pageId, index) => {
        map.set(pageId, index + 1);
      });
      if (row.pageId && !map.has(row.pageId)) map.set(row.pageId, 1);
    });
    return map;
  }, [rows]);

  if (!rows || rows.length === 0) {
    return null;
  }

  return (
    <div className="space-y-6">
      <SourceCard sources={sourcesFromNisEvents(rows)} />

      <Card
        header={
          <h2 className="text-lg font-medium text-gray-900 dark:text-zinc-100">NIS Statement</h2>
        }
      >
        <DescriptionList>
          <DescriptionTerm>Statement Count</DescriptionTerm>
          <DescriptionDetails>{rows.length}</DescriptionDetails>
        </DescriptionList>
      </Card>

      <ExtractionTableCard label="NIS Statement" rows={rows} csvTitle="NIS Statement Events" bodyHeight="project">
        <NisTable rows={rows} pageIdToPosition={pageIdToPosition} onInspect={setInspected} />
      </ExtractionTableCard>

      <RowInspectorDialog
        open={inspected != null}
        onClose={() => setInspected(null)}
        title="NIS Statement row"
        data={inspected}
      />
    </div>
  );
}
